import { memo, useMemo } from "react";

import { curveMonotoneX } from "d3-shape";

import dayjs from "@eisox/dayjs";
import { Chart } from "@eisox/design-system";
import { useMediaQuery } from "@eisox/tools";
import { defineChart, lineY, type ChartPositionScaleOptions } from "@tanstack/charts";
import { d3Curve } from "@tanstack/charts/d3/shape";
import { dot } from "@tanstack/charts/dot";
import { whenFocused } from "@tanstack/charts/focus/mark";
import { scaleLinear } from "@tanstack/charts/scales/linear";

import { appendUnitsToLabels, formatHistoryTooltipDate, type ChartDataPoint } from "~/helpers/history.helper";
import { m } from "~/paraglide/messages";

interface ValveHistoryChartProps {
  data: ChartDataPoint[];
  points: string[];
  unitAxesMapping: Record<string, string[]>;
  labels: Record<string, string>;
  startDate: string;
  onRemoveSeries?: (seriesKey: string) => void;
}

export const ValveHistoryChart = memo(function ValveHistoryChart({
  data,
  points,
  unitAxesMapping,
  labels,
  startDate,
  onRemoveSeries,
}: ValveHistoryChartProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const seriesLabels = useMemo(() => appendUnitsToLabels(labels, unitAxesMapping), [labels, unitAxesMapping]);

  const definition = useMemo(() => {
    const dayStart = dayjs(startDate).startOf("day");
    const unitAxes = Object.values(unitAxesMapping);
    const stepHours = isMobile ? 6 : 3;
    const unitScale = (index: number): ChartPositionScaleOptions<number> => ({
      channel: "y",
      side: index % 2 === 0 ? "left" : "right",
      scale: scaleLinear,
      grid: index === 0 ? Chart.grid : false,
      axis: isMobile
        ? false
        : {
            ...Chart.axis,
            line: { stroke: Chart.palette[points.indexOf(unitAxes[index]?.[0] ?? "") % Chart.palette.length] },
          },
    });

    return defineChart({
      marks: unitAxes.flatMap((seriesKeys, index) => {
        const rows = data.flatMap(row => seriesKeys.map(series => ({ date: row.date, series, value: row[series] })));
        const yScale = index === 0 ? undefined : `unit-${index}`;

        return [
          lineY(rows, { x: "date", y: "value", color: "series", yScale, curve: d3Curve(curveMonotoneX) }),
          whenFocused(dot(rows, { x: "date", y: "value", color: "series", yScale }), { match: "group" }),
        ];
      }),
      color: { domain: points },
      scales: {
        x: {
          scale: scaleLinear().domain([dayStart.valueOf(), dayStart.add(1, "day").valueOf()]),
          axis: {
            ...Chart.axis,
            ticks: {
              ...Chart.axis.ticks,
              values: Array.from({ length: 24 / stepHours + 1 }, (_, step) =>
                dayStart.add(step * stepHours, "hour").valueOf(),
              ),
              format: (value: number) => dayjs(value).format("LT"),
            },
          },
        },
        y: unitScale(0),
        ...Object.fromEntries(unitAxes.slice(1).map((_, index) => [`unit-${index + 1}`, unitScale(index + 1)])),
      },
      focus: "group-x",
      tooltip: {
        ...Chart.tooltip,
        placement: ["right", "left"],
        content: tooltipPoints => ({
          title: formatHistoryTooltipDate(Number(tooltipPoints[0]?.xValue)),
          rows: tooltipPoints.map(point => ({
            label: seriesLabels[point.groupLabel] ?? point.groupLabel,
            value: point.yValue.toLocaleString(),
            color: point.color,
          })),
        }),
      },
    });
  }, [data, isMobile, points, seriesLabels, startDate, unitAxesMapping]);

  return (
    <Chart.Root definition={definition} ariaLabel={m.history()} className="w-full">
      {!isMobile && (
        <Chart.Legend
          label={series => seriesLabels[series] ?? series}
          onRemove={onRemoveSeries}
          removeLabel={name => m.consumption_remove_series({ name })}
        />
      )}
    </Chart.Root>
  );
});
